import { parseAppPath } from '../navigation/appPaths'

/** Canonical login (full screen: Register, Operator login, help). */
export const LOGIN_PATH = '/login'

/** Signed-in app home (operator search / downloads hub). */
export const HOME_PATH = '/home'

/**
 * Marketing / one-click user login — checks session, then redirects to the IdP without the local login UI.
 * Operator login (email + external IdP discovery) stays on `LOGIN_PATH`.
 */
export const LOGIN_START_PATH = '/login/start'

/** Canonical registration (wired in-app links; matches `REGISTRATION_WEB_PATH`). */
export const REGISTER_PATH = '/register'

/** Legacy buxusweb login — redirects to `LOGIN_PATH`. */
export const LEGACY_USER_LOGIN_PATH = '/user/login'

/** Legacy buxusweb registration — same screen as `REGISTER_PATH`; kept for deployed mobile WebView. */
export const LEGACY_USER_REGISTER_PATH = '/user/register'

/** Legacy buxusweb invite — mobile Profile “Invite Responders” WebView entry. */
export const LEGACY_USER_INVITE_PATH = '/user/invite'

const LEGACY_USER_PREFIX = '/user'

function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim() || '/'
  if (trimmed !== '/' && trimmed.endsWith('/')) {
    return trimmed.replace(/\/+$/, '')
  }
  return trimmed
}

export function isLoginPath(pathname: string): boolean {
  return normalizePathname(pathname) === LOGIN_PATH
}

export function isLoginStartPath(pathname: string): boolean {
  return normalizePathname(pathname) === LOGIN_START_PATH
}

/** Safe post-login redirect target from router `location.state.returnTo`. */
export function safeLoginReturnTo(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/')) return HOME_PATH
  const pathOnly = value.split('?')[0] ?? value
  const n = normalizePathname(pathOnly)
  // Scenario Studio — `/scenarios` or `/scenarios/<uuid>`.
  if (n === '/scenarios' || n.startsWith('/scenarios/')) {
    return value
  }
  if (!isAppShellPath(pathOnly)) return HOME_PATH
  return value
}

export function isRegisterPath(pathname: string): boolean {
  return normalizePathname(pathname) === REGISTER_PATH
}

export function isLegacyUserLoginPath(pathname: string): boolean {
  return normalizePathname(pathname) === LEGACY_USER_LOGIN_PATH
}

export function isLegacyUserRegisterPath(pathname: string): boolean {
  return normalizePathname(pathname) === LEGACY_USER_REGISTER_PATH
}

export function isLegacyUserInvitePath(pathname: string): boolean {
  return normalizePathname(pathname) === LEGACY_USER_INVITE_PATH
}

/** `/user` with no further segment — legacy redirects this to login. */
export function isLegacyUserRootPath(pathname: string): boolean {
  return normalizePathname(pathname) === LEGACY_USER_PREFIX
}

/** Any `/user/...` path (including login/register aliases). */
export function isLegacyUserPath(pathname: string): boolean {
  const n = normalizePathname(pathname)
  return n === LEGACY_USER_PREFIX || n.startsWith(`${LEGACY_USER_PREFIX}/`)
}

export function isPublicAuthPath(pathname: string): boolean {
  const n = normalizePathname(pathname)
  return (
    n === LOGIN_PATH ||
    n === LOGIN_START_PATH ||
    n === REGISTER_PATH ||
    n === LEGACY_USER_REGISTER_PATH ||
    n === LEGACY_USER_INVITE_PATH
  )
}

/**
 * Unknown `/user/*` paths (e.g. `/user/saldfkjadsfj`) must not fall through to login.
 */
export function isUnsupportedLegacyUserPath(pathname: string): boolean {
  if (!isLegacyUserPath(pathname)) return false
  if (isLegacyUserRootPath(pathname)) return false
  if (isLegacyUserLoginPath(pathname)) return false
  if (isLegacyUserRegisterPath(pathname)) return false
  if (isLegacyUserInvitePath(pathname)) return false
  return true
}

/** Paths handled by the signed-in operator shell (`App`). */
export function isAppShellPath(pathname: string): boolean {
  if (isUnsupportedLegacyUserPath(pathname)) return false
  if (isLegacyUserPath(pathname)) return false
  if (isPublicAuthPath(pathname)) return false
  const n = normalizePathname(pathname)
  if (n === '/') return true
  return parseAppPath(n) !== null
}
